import { readStoredJson, writeStored, writeStoredJson } from './storage'
import type { TranscriptionJob, TranscriptionRetryRequest } from './types'

interface PendingRequest {
  payload: string
  key: string
}

// Keep retries stable within the tab when browser storage is unavailable.
const pendingRequests = new Map<string, PendingRequest>()

/** A replay after an ambiguous response keeps the same key, even after reload. */
export function transcriptionRequestKey(storageKey: string, body: unknown): string {
  const payload = JSON.stringify(body)
  const pending = pendingRequests.get(storageKey) ?? readStoredJson<PendingRequest | null>(storageKey, null)
  if (pending?.payload === payload && /^[a-f0-9]{32}$/.test(pending.key)) {
    pendingRequests.set(storageKey, pending)
    return pending.key
  }
  // getRandomValues also works on self-hosted HTTP consoles where randomUUID
  // is unavailable. Never replace a durable key just because a call timed out.
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  const key = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  pendingRequests.set(storageKey, { payload, key })
  writeStoredJson(storageKey, { payload, key })
  return key
}

export function clearTranscriptionRequest(storageKey: string): void {
  pendingRequests.delete(storageKey)
  writeStored(storageKey, null)
}

export function isUncertainTranscription(job: Pick<TranscriptionJob, 'state'>): boolean {
  return job.state === 'submission_unknown' || job.state === 'recovery_window_exceeded'
}

const SETTLED_TRANSCRIPTION_STATES: ReadonlySet<string> = new Set(['succeeded', 'failed'])

/** A job that will not change again without a new request. */
export function isTranscriptionSettled(state: string): boolean {
  return SETTLED_TRANSCRIPTION_STATES.has(state)
}

export function paidRetryRequest(
  job: Pick<TranscriptionJob, 'state' | 'current_attempt_id'>,
): TranscriptionRetryRequest {
  return {
    mode: 'resubmit',
    expected_attempt_id: job.current_attempt_id,
    confirm_new_paid_attempt: true,
    ...(isUncertainTranscription(job) ? { acknowledge_possible_duplicate_charge: true as const } : {}),
  }
}

export function transcriptionFilename(disposition: string | null): string | null {
  if (!disposition || !/^attachment\s*;/i.test(disposition)) return null
  const extended = /(?:^|;)\s*filename\*=UTF-8''([^;]+)/i.exec(disposition)
  const plain = /(?:^|;)\s*filename=(?:"([^"]+)"|([^;\s]+))/i.exec(disposition)
  let filename = extended?.[1] ?? plain?.[1] ?? plain?.[2] ?? ''
  if (extended) {
    try {
      filename = decodeURIComponent(filename)
    } catch {
      return null
    }
  }
  return /^\d{1,32}\.md$/.test(filename) ? filename : null
}
